import { createBrowserRouter, Navigate } from 'react-router-dom';
import React, { lazy, Suspense } from 'react';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { JoinRedirect } from './JoinRedirect';

// Lightweight loading component for initial load
function PageLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-gold border-t-transparent" />
    </div>
  );
}

// Simple error boundary for route errors
function PageError({ title = 'Page Not Found' }: { title?: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>
        <p className="text-gray-600 mb-4">The page you're looking for doesn't exist.</p>
        <a href="/" className="text-gold hover:underline">Go back home</a>
      </div>
    </div>
  );
}

// Lazy load layouts
const MemberLayout = lazy(() => import('@/components/layout/MemberLayout').then(m => ({ default: m.MemberLayout })));
const ProtectedRoute = lazy(() => import('./ProtectedRoute').then(m => ({ default: m.ProtectedRoute })));

// Lazy load public pages
const HomePage = lazy(() => import('@/pages/public/HomePage'));
const EventsPage = lazy(() => import('@/pages/public/EventsPage'));
const EventDetailPage = lazy(() => import('@/pages/public/EventDetailPage'));
const ArticlesPage = lazy(() => import('@/pages/public/ArticlesPage'));
const ArticleDetailPage = lazy(() => import('@/pages/public/ArticleDetailPage'));
const SponsorsPage = lazy(() => import('@/pages/public/SponsorsPage'));
const SponsorDetailPage = lazy(() => import('@/pages/public/SponsorDetailPage'));
const AboutPage = lazy(() => import('@/pages/public/AboutPage'));
const AboutTheTeamPage = lazy(() => import('@/pages/public/AboutTheTeamPage'));
const PastEventsPage = lazy(() => import('@/pages/public/PastEventsPage'));
const PastEventDetailPage = lazy(() => import('@/pages/public/PastEventDetailPage'));
const ContactPage = lazy(() => import('@/pages/public/ContactPage'));
const MembershipPage = lazy(() => import('@/pages/public/MembershipPage'));
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const ApplyPage = lazy(() => import('@/pages/public/Apply'));
const CompetitionPage = lazy(() => import('@/pages/public/CompetitionPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const LegalPage = lazy(() => import('@/pages/public/LegalPage'));
const SponsorApplicationPage = lazy(() => import('@/pages/public/SponsorApplicationPage'));
const SponsorPackPage = lazy(() => import('@/pages/public/SponsorPackPage'));
const LandingPage = lazy(() => import('@/pages/public/LandingPage'));
const MemberProfilePage = lazy(() => import('@/pages/public/MemberProfilePage'));

// Lazy load member pages
const DashboardPage = lazy(() => import('@/pages/members/DashboardPage'));
const VehiclesPage = lazy(() => import('@/pages/members/VehiclesPage'));
const AddVehiclePage = lazy(() => import('@/pages/members/AddVehiclePage'));
const EditVehiclePage = lazy(() => import('@/pages/members/EditVehiclePage'));
const BookingsPage = lazy(() => import('@/pages/members/BookingsPage'));
const BookEventPage = lazy(() => import('@/pages/members/BookEventPage'));
const ProfilePage = lazy(() => import('@/pages/members/ProfilePage'));
const ChangeEmailPage = lazy(() => import('@/pages/members/ChangeEmailPage'));
const ChangePasswordPage = lazy(() => import('@/pages/members/ChangePasswordPage'));
const EmergencyContactPage = lazy(() => import('@/pages/members/EmergencyContactPage'));
const PassengersPage = lazy(() => import('@/pages/members/PassengersPage'));
const TestimonialPage = lazy(() => import('@/pages/members/TestimonialPage'));
const AboutMePage = lazy(() => import('@/pages/members/AboutMePage'));
const CreditsPage = lazy(() => import('@/pages/members/CreditsPage'));
const InvitePage = lazy(() => import('@/pages/members/InvitePage'));
const PollsPage = lazy(() => import('@/pages/members/PollsPage'));
const PollVotePage = lazy(() => import('@/pages/members/PollVotePage'));
const PollResultsPage = lazy(() => import('@/pages/members/PollResultsPage'));
const QRCodePage = lazy(() => import('@/pages/members/QRCodePage'));
const CompetitionsPage = lazy(() => import('@/pages/members/CompetitionsPage'));
const MyCarPhotosPage = lazy(() => import('@/pages/members/MyCarPhotosPage'));
const PaymentSuccessPage = lazy(() => import('@/pages/members/PaymentSuccessPage'));
const PaymentCancelledPage = lazy(() => import('@/pages/members/PaymentCancelledPage'));
const MembershipSelectPage = lazy(() => import('@/pages/members/MembershipSelectPage'));
const CheckoutPage = lazy(() => import('@/pages/members/CheckoutPage'));
const PaymentConfirmingPage = lazy(() => import('@/pages/members/PaymentConfirmingPage'));
const OrderSummaryPage = lazy(() => import('@/pages/members/OrderSummaryPage'));
const MyMembershipPage = lazy(() => import('@/pages/members/MyMembershipPage'));

// Lazy load admin module entirely
const AdminLayout = lazy(() => import('@/pages/admin/AdminLayout'));
const AdminDashboardPage = lazy(() => import('@/pages/admin/DashboardPage'));
const AdminEventsListPage = lazy(() => import('@/pages/admin/events/EventsListPage'));
const AdminEventCreatePage = lazy(() => import('@/pages/admin/events/EventCreatePage'));
const AdminEventDetailPage = lazy(() => import('@/pages/admin/events/EventDetailPage'));
const AdminEventEditPage = lazy(() => import('@/pages/admin/events/EventEditPage'));
const AdminEventOptionsPage = lazy(() => import('@/pages/admin/events/EventOptionsPage'));
const AdminEventComponentsPage = lazy(() => import('@/pages/admin/events/EventComponentsPage'));
const AdminEventBookingsPage = lazy(() => import('@/pages/admin/events/EventBookingsPage'));
// PCE-399: Admin consent tracking / chase page
const AdminEventConsentPage = lazy(() => import('@/pages/admin/events/EventConsentPage'));
const AdminEventImagesPage = lazy(() => import('@/pages/admin/events/EventImagesPage'));
const AdminEventUploadsPage = lazy(() => import('@/pages/admin/events/EventUploadsPage'));
const AdminEventPlanningPage = lazy(() => import('@/pages/admin/events/EventPlanningPage'));
const AdminKeyDateListPage = lazy(() => import('@/pages/admin/key-dates/KeyDateListPage'));
const AdminKeyDateFormPage = lazy(() => import('@/pages/admin/key-dates/KeyDateFormPage'));
const AdminMemberListPage = lazy(() => import('@/pages/admin/members/MemberListPage'));
const AdminMemberDetailPage = lazy(() => import('@/pages/admin/members/MemberDetailPage'));
const AdminMemberFormPage = lazy(() => import('@/pages/admin/members/MemberFormPage'));
const AdminRequestListPage = lazy(() => import('@/pages/admin/member-requests/RequestListPage'));
const AdminRequestDetailPage = lazy(() => import('@/pages/admin/member-requests/RequestDetailPage'));
const AdminCheckInLayout = lazy(() => import('@/pages/admin/check-in/CheckInLayout'));
const AdminEventSelectPage = lazy(() => import('@/pages/admin/check-in/EventSelectPage'));
const AdminCheckInPage = lazy(() => import('@/pages/admin/check-in/CheckInPage'));
// PCE-356: mobile meal reconciliation (Photograph Receipt) screen
const AdminMealReceiptPage = lazy(() => import('@/pages/admin/check-in/meal-receipt/MealReceiptPage'));
// PCE-357: Confirm & settle screen for a Meal component
const AdminMealSettlementPage = lazy(() => import('@/pages/admin/check-in/meal-settlement/MealSettlementPage'));
const AdminCompetitionListPage = lazy(() => import('@/pages/admin/competitions/CompetitionListPage'));
const AdminCompetitionDetailPage = lazy(() => import('@/pages/admin/competitions/CompetitionDetailPage'));
const AdminCompetitionFormPage = lazy(() => import('@/pages/admin/competitions/CompetitionFormPage'));
const AdminPollListPage = lazy(() => import('@/pages/admin/polls/PollListPage'));
const AdminPollDetailPage = lazy(() => import('@/pages/admin/polls/PollDetailPage'));
const AdminPollFormPage = lazy(() => import('@/pages/admin/polls/PollFormPage'));
const AdminSponsorListPage = lazy(() => import('@/pages/admin/sponsors/SponsorListPage'));
const AdminSponsorDetailPage = lazy(() => import('@/pages/admin/sponsors/SponsorDetailPage'));
const AdminSponsorFormPage = lazy(() => import('@/pages/admin/sponsors/SponsorFormPage'));
const AdminCreditRuleListPage = lazy(() => import('@/pages/admin/credits/CreditRuleListPage'));
const AdminCreditRuleFormPage = lazy(() => import('@/pages/admin/credits/CreditRuleFormPage'));
const AdminCreditListPage = lazy(() => import('@/pages/admin/credits/CreditListPage'));
const AdminTestimonialListPage = lazy(() => import('@/pages/admin/testimonials/TestimonialListPage'));
const AdminTestimonialFormPage = lazy(() => import('@/pages/admin/testimonials/TestimonialFormPage'));
const AdminTestimonialDetailPage = lazy(() => import('@/pages/admin/testimonials/TestimonialDetailPage'));
const AdminTeamMemberListPage = lazy(() => import('@/pages/admin/team/TeamMemberListPage'));
const AdminTeamMemberFormPage = lazy(() => import('@/pages/admin/team/TeamMemberFormPage'));
const AdminInvitationListPage = lazy(() => import('@/pages/admin/invitations/InvitationListPage'));
const AdminInvitationDetailPage = lazy(() => import('@/pages/admin/invitations/InvitationDetailPage'));
const AdminVenueListPage = lazy(() => import('@/pages/admin/venues/VenueListPage'));
const AdminVenueDetailPage = lazy(() => import('@/pages/admin/venues/VenueDetailPage'));
const AdminVenueFormPage = lazy(() => import('@/pages/admin/venues/VenueFormPage'));
const AdminRouteListPage = lazy(() => import('@/pages/admin/routes/RouteListPage'));
const AdminRouteDetailPage = lazy(() => import('@/pages/admin/routes/RouteDetailPage'));
const AdminRouteFormPage = lazy(() => import('@/pages/admin/routes/RouteFormPage'));
const AdminEventTypeListPage = lazy(() => import('@/pages/admin/event-types/EventTypeListPage'));
const AdminEventTypeDetailPage = lazy(() => import('@/pages/admin/event-types/EventTypeDetailPage'));
const AdminEventTypeFormPage = lazy(() => import('@/pages/admin/event-types/EventTypeFormPage'));
const AdminProductListPage = lazy(() => import('@/pages/admin/products/ProductListPage'));
const AdminProductDetailPage = lazy(() => import('@/pages/admin/products/ProductDetailPage'));
const AdminProductFormPage = lazy(() => import('@/pages/admin/products/ProductFormPage'));
const AdminCategoryListPage = lazy(() => import('@/pages/admin/products/CategoryListPage'));
const AdminCategoryFormPage = lazy(() => import('@/pages/admin/products/CategoryFormPage'));
const AdminOrderListPage = lazy(() => import('@/pages/admin/products/OrderListPage'));
const AdminOrderDetailPage = lazy(() => import('@/pages/admin/products/OrderDetailPage'));

// Lazy load meal pre-order page (T038)
const PreOrderFormPage = lazy(() => import('@/pages/members/events/PreOrderFormPage'));

// Lazy load member "my tab" wizard (PCE-340)
const MealTabPage = lazy(() => import('@/pages/members/events/tab/MealTabPage'));

// Lazy load member booking consent screen (PCE-396/397/398)
const BookingConsentPage = lazy(() => import('@/pages/members/events/BookingConsentPage'));

// Lazy load merchandise member pages (T059)
const MerchandiseListPage = lazy(() => import('@/pages/members/merchandise/MerchandiseListPage'));
const MerchandiseProductDetailPage = lazy(() => import('@/pages/members/merchandise/ProductDetailPage'));
const MerchandiseBasketPage = lazy(() => import('@/pages/members/merchandise/BasketPage'));

// Lazy load order pages (T068-T077)
const MemberOrderHistoryPage = lazy(() => import('@/pages/members/orders/OrderHistoryPage'));
const MemberOrderDetailPage = lazy(() => import('@/pages/members/orders/OrderDetailPage'));
const OrderConfirmationPage = lazy(() => import('@/pages/members/checkout/OrderConfirmationPage'));
const AdminMembershipTypeListPage = lazy(() => import('@/pages/admin/membershipTypes/MembershipTypeListPage'));
const AdminMembershipTypeFormPage = lazy(() => import('@/pages/admin/membershipTypes/MembershipTypeFormPage'));
const AdminMembershipTypeDetailPage = lazy(() => import('@/pages/admin/membershipTypes/MembershipTypeDetailPage'));
const AdminMembershipFeaturesMatrixPage = lazy(() => import('@/pages/admin/membershipTypes/MembershipFeaturesMatrixPage'));
const AdminContactsListPage = lazy(() => import('@/pages/admin/contacts/ContactsListPage'));
const AdminContactDetailPage = lazy(() => import('@/pages/admin/contacts/ContactDetailPage'));
const AdminVehicleMakeListPage = lazy(() => import('@/pages/admin/vehicle-makes/VehicleMakeListPage'));
const AdminVehicleMakeDetailPage = lazy(() => import('@/pages/admin/vehicle-makes/VehicleMakeDetailPage'));
const AdminInstallmentsPage = lazy(() => import('@/pages/admin/installments/InstallmentsPage'));
const AdminPageViewsPage = lazy(() => import('@/pages/admin/pageviews/PageViewsPage'));


// Error boundary for lazy loading failures
class LazyLoadErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback?: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error('Lazy loading error:', error);
    // Reload the page to get fresh chunks
    if (error.message.includes('Failed to fetch') || error.message.includes('Importing a module')) {
      console.log('Reloading page to fetch fresh resources...');
      window.location.reload();
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || <PageError title="Loading Error" />;
    }
    return this.props.children;
  }
}

// Helper to wrap lazy components with Suspense and error boundary
function withSuspense(Component: React.LazyExoticComponent<React.ComponentType>) {
  return (
    <LazyLoadErrorBoundary>
      <Suspense fallback={<PageLoading />}>
        <Component />
      </Suspense>
    </LazyLoadErrorBoundary>
  );
}

export const router = createBrowserRouter([
  // Admin routes - fully lazy loaded
  {
    path: '/admin',
    element: withSuspense(AdminLayout),
    children: [
      { index: true, element: withSuspense(AdminDashboardPage) },
      {
        path: 'events',
        children: [
          { index: true, element: withSuspense(AdminEventsListPage) },
          { path: 'new', element: withSuspense(AdminEventCreatePage) },
          { path: 'planning', element: withSuspense(AdminEventPlanningPage) },
          { path: ':id', element: withSuspense(AdminEventDetailPage) },
          { path: ':id/edit', element: withSuspense(AdminEventEditPage) },
          { path: ':id/components', element: withSuspense(AdminEventComponentsPage) },
          { path: ':id/options', element: withSuspense(AdminEventOptionsPage) },
          { path: ':id/bookings', element: withSuspense(AdminEventBookingsPage) },
          { path: ':id/consent', element: withSuspense(AdminEventConsentPage) },
          { path: ':id/images', element: withSuspense(AdminEventImagesPage) },
          { path: ':id/uploads', element: withSuspense(AdminEventUploadsPage) },
        ],
      },
      {
        path: 'key-dates',
        children: [
          { index: true, element: withSuspense(AdminKeyDateListPage) },
          { path: 'new', element: withSuspense(AdminKeyDateFormPage) },
          { path: ':id/edit', element: withSuspense(AdminKeyDateFormPage) },
        ],
      },
      {
        path: 'members',
        children: [
          { index: true, element: withSuspense(AdminMemberListPage) },
          { path: 'new', element: withSuspense(AdminMemberFormPage) },
          { path: ':id', element: withSuspense(AdminMemberDetailPage) },
          { path: ':id/edit', element: withSuspense(AdminMemberFormPage) },
        ],
      },
      {
        path: 'member-requests',
        children: [
          { index: true, element: withSuspense(AdminRequestListPage) },
          { path: ':id', element: withSuspense(AdminRequestDetailPage) },
        ],
      },
      {
        path: 'event-day',
        element: withSuspense(AdminCheckInLayout),
        children: [
          { index: true, element: withSuspense(AdminEventSelectPage) },
          { path: ':eventId', element: withSuspense(AdminCheckInPage) },
          // PCE-356: mobile meal reconciliation (Photograph Receipt) for a Meal component
          { path: ':eventId/meal/:componentId/receipt', element: withSuspense(AdminMealReceiptPage) },
          // PCE-357: Confirm & settle for a Meal component
          { path: ':eventId/meal/:componentId/settle', element: withSuspense(AdminMealSettlementPage) },
        ],
      },
      {
        path: 'competitions',
        children: [
          { index: true, element: withSuspense(AdminCompetitionListPage) },
          { path: 'new', element: withSuspense(AdminCompetitionFormPage) },
          { path: ':id', element: withSuspense(AdminCompetitionDetailPage) },
          { path: ':id/edit', element: withSuspense(AdminCompetitionFormPage) },
        ],
      },
      {
        path: 'polls',
        children: [
          { index: true, element: withSuspense(AdminPollListPage) },
          { path: 'new', element: withSuspense(AdminPollFormPage) },
          { path: ':id', element: withSuspense(AdminPollDetailPage) },
          { path: ':id/edit', element: withSuspense(AdminPollFormPage) },
        ],
      },
      {
        path: 'sponsors',
        children: [
          { index: true, element: withSuspense(AdminSponsorListPage) },
          { path: 'new', element: withSuspense(AdminSponsorFormPage) },
          { path: ':id', element: withSuspense(AdminSponsorDetailPage) },
          { path: ':id/edit', element: withSuspense(AdminSponsorFormPage) },
        ],
      },
      {
        path: 'credit-rules',
        children: [
          { index: true, element: withSuspense(AdminCreditRuleListPage) },
          { path: 'new', element: withSuspense(AdminCreditRuleFormPage) },
          { path: ':id/edit', element: withSuspense(AdminCreditRuleFormPage) },
        ],
      },
      { path: 'credits', element: withSuspense(AdminCreditListPage) },
      { path: 'installments', element: withSuspense(AdminInstallmentsPage) },
      {
        path: 'testimonials',
        children: [
          { index: true, element: withSuspense(AdminTestimonialListPage) },
          { path: 'new', element: withSuspense(AdminTestimonialFormPage) },
          { path: ':id', element: withSuspense(AdminTestimonialDetailPage) },
          { path: ':id/edit', element: withSuspense(AdminTestimonialFormPage) },
        ],
      },
      {
        path: 'team-members',
        children: [
          { index: true, element: withSuspense(AdminTeamMemberListPage) },
          { path: 'new', element: withSuspense(AdminTeamMemberFormPage) },
          { path: ':id/edit', element: withSuspense(AdminTeamMemberFormPage) },
        ],
      },
      {
        path: 'invitations',
        children: [
          { index: true, element: withSuspense(AdminInvitationListPage) },
          { path: ':id', element: withSuspense(AdminInvitationDetailPage) },
        ],
      },
      {
        path: 'venues',
        children: [
          { index: true, element: withSuspense(AdminVenueListPage) },
          { path: 'new', element: withSuspense(AdminVenueFormPage) },
          { path: ':id', element: withSuspense(AdminVenueDetailPage) },
          { path: ':id/edit', element: withSuspense(AdminVenueFormPage) },
        ],
      },
      {
        path: 'routes',
        children: [
          { index: true, element: withSuspense(AdminRouteListPage) },
          { path: 'new', element: withSuspense(AdminRouteFormPage) },
          { path: ':id', element: withSuspense(AdminRouteDetailPage) },
          { path: ':id/edit', element: withSuspense(AdminRouteFormPage) },
        ],
      },
      {
        path: 'event-types',
        children: [
          { index: true, element: withSuspense(AdminEventTypeListPage) },
          { path: 'new', element: withSuspense(AdminEventTypeFormPage) },
          { path: ':id', element: withSuspense(AdminEventTypeDetailPage) },
          { path: ':id/edit', element: withSuspense(AdminEventTypeFormPage) },
        ],
      },
      {
        path: 'products',
        children: [
          { index: true, element: withSuspense(AdminProductListPage) },
          { path: 'new', element: withSuspense(AdminProductFormPage) },
          { path: ':id', element: withSuspense(AdminProductDetailPage) },
          { path: ':id/edit', element: withSuspense(AdminProductFormPage) },
          {
            path: 'categories',
            children: [
              { index: true, element: withSuspense(AdminCategoryListPage) },
              { path: 'new', element: withSuspense(AdminCategoryFormPage) },
              { path: ':id/edit', element: withSuspense(AdminCategoryFormPage) },
            ],
          },
          {
            path: 'orders',
            children: [
              { index: true, element: withSuspense(AdminOrderListPage) },
              { path: ':id', element: withSuspense(AdminOrderDetailPage) },
            ],
          },
        ],
      },
      {
        path: 'membership-types',
        children: [
          { index: true, element: withSuspense(AdminMembershipTypeListPage) },
          { path: 'new', element: withSuspense(AdminMembershipTypeFormPage) },
          { path: ':id', element: withSuspense(AdminMembershipTypeDetailPage) },
          { path: ':id/edit', element: withSuspense(AdminMembershipTypeFormPage) },
        ],
      },
      { path: 'membership-features', element: withSuspense(AdminMembershipFeaturesMatrixPage) },
      {
        path: 'contacts',
        children: [
          { index: true, element: withSuspense(AdminContactsListPage) },
          { path: ':id', element: withSuspense(AdminContactDetailPage) },
        ],
      },
      {
        path: 'vehicle-makes',
        children: [
          { index: true, element: withSuspense(AdminVehicleMakeListPage) },
          { path: 'new', element: withSuspense(AdminVehicleMakeDetailPage) },
          { path: ':id', element: withSuspense(AdminVehicleMakeDetailPage) },
        ],
      },
      { path: 'page-views', element: withSuspense(AdminPageViewsPage) },
    ],
  },

  // Public routes with PublicLayout
  {
    path: '/',
    element: <PublicLayout />,
    errorElement: <PageError title="Page Not Found" />,
    children: [
      { index: true, element: withSuspense(HomePage) },
      // Events
      { path: 'events', element: withSuspense(EventsPage) },
      { path: 'events/:year/:slug', element: withSuspense(EventDetailPage) },
      { path: 'events/:slug', element: withSuspense(EventDetailPage) },
      // Past/Previous Events
      { path: 'previous-events', element: withSuspense(PastEventsPage) },
      { path: 'previous-events/:year/:slug', element: withSuspense(PastEventDetailPage) },
      // Articles
      { path: 'articles', element: withSuspense(ArticlesPage) },
      { path: 'articles/:slug', element: withSuspense(ArticleDetailPage) },
      { path: 'articles/:category/:slug', element: withSuspense(ArticleDetailPage) },
      // Legacy blog routes
      { path: 'blog', element: <Navigate to="/articles" replace /> },
      { path: 'blog/:slug', element: <Navigate to="/articles/:slug" replace /> },
      // Sponsors
      { path: 'sponsors', element: withSuspense(SponsorsPage) },
      { path: 'sponsors/:slug', element: withSuspense(SponsorDetailPage) },
      // Static pages
      { path: 'about-us', element: withSuspense(AboutPage) },
      { path: 'about', element: <Navigate to="/about-us" replace /> },
      { path: 'about-the-team', element: withSuspense(AboutTheTeamPage) },
      { path: 'about-the-founders', element: <Navigate to="/about-the-team" replace /> },
      { path: 'contact', element: withSuspense(ContactPage) },
      { path: 'membership', element: withSuspense(MembershipPage) },
      // Legal pages - single dynamic route handles all terms pages
      { path: 'terms', element: <Navigate to="/terms/conditions" replace /> },
      { path: 'terms/:slug', element: withSuspense(LegalPage) },
      // Auth pages
      { path: 'login', element: withSuspense(LoginPage) },
      { path: 'auth/login', element: withSuspense(LoginPage) },
      { path: 'join', element: <Navigate to="/apply" replace /> },
      { path: 'join/:inviteCode', element: <JoinRedirect /> },
      { path: 'forgot-password', element: withSuspense(ForgotPasswordPage) },
      { path: 'auth/forgot-password', element: withSuspense(ForgotPasswordPage) },
      { path: 'reset-password', element: withSuspense(ResetPasswordPage) },
      { path: 'auth/reset-password', element: withSuspense(ResetPasswordPage) },
      { path: 'register', element: withSuspense(RegisterPage) },
      // Application flow
      { path: 'apply', element: withSuspense(ApplyPage) },
      { path: 'apply-two', element: withSuspense(ApplyPage) },
      { path: 'apply-complete', element: withSuspense(ApplyPage) },
      { path: 'applied', element: withSuspense(ApplyPage) },
      // Competition entry
      { path: 'competition/:url', element: withSuspense(CompetitionPage) },
      // Sponsor application
      { path: 'sponsor-application', element: withSuspense(SponsorApplicationPage) },
      { path: 'sponsor-pack', element: withSuspense(SponsorPackPage) },
      // Landing page
      { path: 'landing', element: withSuspense(LandingPage) },
      // Public member profile
      { path: 'members/:memberId/profile', element: withSuspense(MemberProfilePage) },
    ],
  },

  // Root-level invite route (authenticated)
  {
    path: '/invite',
    element: (
      <Suspense fallback={<PageLoading />}>
        <ProtectedRoute>
          <PublicLayout />
        </ProtectedRoute>
      </Suspense>
    ),
    children: [
      { index: true, element: withSuspense(InvitePage) },
    ],
  },

  // Event payment callback routes
  {
    path: '/events/:id/paymentsuccess',
    element: (
      <Suspense fallback={<PageLoading />}>
        <ProtectedRoute>
          <PublicLayout />
        </ProtectedRoute>
      </Suspense>
    ),
    children: [
      { index: true, element: withSuspense(PaymentSuccessPage) },
    ],
  },
  {
    path: '/events/:id/paymentcancelled',
    element: <PublicLayout />,
    children: [
      { index: true, element: withSuspense(PaymentCancelledPage) },
    ],
  },

  // Member routes with MemberLayout (protected)
  {
    path: '/members',
    element: (
      <Suspense fallback={<PageLoading />}>
        <ProtectedRoute>
          <MemberLayout />
        </ProtectedRoute>
      </Suspense>
    ),
    errorElement: <PageError title="Page Not Found" />,
    children: [
      { index: true, element: withSuspense(DashboardPage) },
      { path: 'vehicles', element: withSuspense(VehiclesPage) },
      { path: 'vehicles/add', element: withSuspense(AddVehiclePage) },
      { path: 'vehicles/:id/edit', element: withSuspense(EditVehiclePage) },
      { path: 'bookings', element: withSuspense(BookingsPage) },
      { path: 'events/:year/:slug/book', element: withSuspense(BookEventPage) },
      { path: 'events/:slug/book', element: withSuspense(BookEventPage) },
      // T038: Meal pre-order form
      { path: 'events/:eventId/components/:componentId/pre-order', element: withSuspense(PreOrderFormPage) },
      // PCE-340: Member "my tab" wizard
      { path: 'events/:eventId/components/:componentId/tab', element: withSuspense(MealTabPage) },
      // PCE-396/397/398: Member consent screen (also the emailed deep-link target)
      { path: 'events/:eventId/bookings/:bookingId/consent', element: withSuspense(BookingConsentPage) },
      { path: 'profile', element: withSuspense(ProfilePage) },
      { path: 'change-email', element: withSuspense(ChangeEmailPage) },
      { path: 'change-password', element: withSuspense(ChangePasswordPage) },
      { path: 'emergency-contact', element: withSuspense(EmergencyContactPage) },
      { path: 'passengers', element: withSuspense(PassengersPage) },
      { path: 'testimonial', element: withSuspense(TestimonialPage) },
      { path: 'about-me', element: withSuspense(AboutMePage) },
      { path: 'credits', element: withSuspense(CreditsPage) },
      { path: 'invite', element: withSuspense(InvitePage) },
      { path: 'qrcode', element: withSuspense(QRCodePage) },
      { path: 'polls', element: withSuspense(PollsPage) },
      { path: 'polls/:id/vote', element: withSuspense(PollVotePage) },
      { path: 'polls/:id/results', element: withSuspense(PollResultsPage) },
      { path: 'competitions', element: withSuspense(CompetitionsPage) },
      { path: 'photos', element: withSuspense(MyCarPhotosPage) },
      { path: 'checkout/:basketId', element: withSuspense(CheckoutPage) },
      { path: 'checkout/:basketId/confirming', element: withSuspense(PaymentConfirmingPage) },
      { path: 'order-summary/:basketId', element: withSuspense(OrderSummaryPage) },
      { path: 'membership', element: withSuspense(MyMembershipPage) },
      { path: 'membership/select', element: withSuspense(MembershipSelectPage) },
      // T059: Merchandise / Shop routes
      { path: 'shop', element: withSuspense(MerchandiseListPage) },
      { path: 'shop/basket', element: withSuspense(MerchandiseBasketPage) },
      { path: 'shop/:id', element: withSuspense(MerchandiseProductDetailPage) },
      // T072-T076: Order history and confirmation pages
      { path: 'orders', element: withSuspense(MemberOrderHistoryPage) },
      { path: 'orders/:id', element: withSuspense(MemberOrderDetailPage) },
      { path: 'checkout/confirmation/:orderId', element: withSuspense(OrderConfirmationPage) },
    ],
  },

  // Catch-all 404
  {
    path: '*',
    element: <PageError title="Page Not Found" />,
  },
]);
